import { useCallback, useEffect, useRef, useState } from "react";

/**
 * `useState` that persists to `localStorage`. Generic and dependency-light: a key + an initial value,
 * back like `useState`. The value is JSON-serialized; reads/writes are wrapped in try/catch so a private
 * window, a disabled storage quota, or a malformed stored value never throws — it falls back to
 * `initialValue` and the component still renders.
 *
 * The setter accepts a value or an updater (mirroring `useState`), and cross-tab `storage` events keep
 * the value in sync when another tab writes the same key.
 *
 * The third element REMOVES the key and returns state to `initialValue`. It exists because writing the
 * initial value back is not the same thing: a "cleared" preference then persists as a literal stored
 * value (`"null"`), which is indistinguishable from a user who chose that value, and it leaves the key
 * behind forever. Reach for it whenever "revert to default" is a real user action.
 */
export function useLocalStorageState<T>(key: string, initialValue: T): [T, (next: T | ((prev: T) => T)) => void, () => void] {
	// `initialValue` is usually an inline literal, so a fresh identity every render. Held in a ref so
	// `readValue` and `removeValue` can use it without making themselves unstable.
	const initialValueRef = useRef(initialValue);
	initialValueRef.current = initialValue;

	const readValue = useCallback((storageKey: string): T => {
		try {
			const raw = window.localStorage.getItem(storageKey);
			return raw === null ? initialValueRef.current : (JSON.parse(raw) as T);
		} catch {
			// Private-mode / quota / malformed JSON — behave as if nothing was stored.
			return initialValueRef.current;
		}
	}, []);

	/**
	 * The key is held WITH the value so a key change resolves in the render it happens on rather than in
	 * an effect one render later. That gap was real, not theoretical: a caller whose key is derived from
	 * fetched data (the hiring page's job id) rendered the PREVIOUS key's value for one frame after the
	 * payload landed, showing the server's un-edited list, and any write landing in that frame would fork
	 * from the wrong value and overwrite the new key's stored edits.
	 */
	const [stored, setStored] = useState<{ key: string; value: T }>(() => ({ key, value: readValue(key) }));
	const current = stored.key === key ? stored : { key, value: readValue(key) };
	if (current !== stored) {
		// React's documented "adjusting state when a prop changes": it discards this render's output and
		// immediately re-renders with the new key's value, so nothing downstream ever sees the stale one.
		setStored(current);
	}

	const setStoredValue = useCallback(
		(next: T | ((prev: T) => T)) => {
			setStored((prev) => {
				// A key change resolves during render, so `prev` is this key's value — read fresh rather
				// than trust it if a future change ever breaks that ordering.
				const base = prev.key === key ? prev.value : readValue(key);
				const resolved = next instanceof Function ? next(base) : next;
				try {
					window.localStorage.setItem(key, JSON.stringify(resolved));
				} catch {
					// Persisting failed (quota/private mode) — keep the in-memory value so the UI still updates.
				}
				return { key, value: resolved };
			});
		},
		[key, readValue],
	);

	const removeValue = useCallback(() => {
		try {
			window.localStorage.removeItem(key);
		} catch {
			// Private-mode / quota — the in-memory reset below still happens, so the UI is consistent.
		}
		setStored({ key, value: initialValueRef.current });
	}, [key]);

	// Keep in sync when another tab writes the same key.
	useEffect(() => {
		const onStorage = (event: StorageEvent) => {
			if (event.key === key) {
				setStored({ key, value: readValue(key) });
			}
		};
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, [key, readValue]);

	return [current.value, setStoredValue, removeValue];
}

export default useLocalStorageState;
